import React, { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faEye, faEyeSlash, faTimes } from '@fortawesome/free-solid-svg-icons';
import "../styles/companyModel.css";

function CompanyModal({
    isOpen,
    onClose,
    onContinue,
    customer,
    user,
    isMobile,
    t = (text) => text,
    crDuplicateCount = 0,
    crDuplicateCustomer = [],
    vatDuplicateCustomer = [],
    vatDuplicateCount = 0,
    isApproved = false,
    customerData
}) {
    const [showDuplicateDetails, setShowDuplicateDetails] = useState(true);

    if (!isOpen) return null;

    const data = customer || {};
    const hasDuplicates = crDuplicateCount > 0 || vatDuplicateCount > 0;

    const address = [
        data.buildingName,
        data.street,
        data.district,
        data.city,
        data.region,
        data.pincode,
    ]
        .filter(Boolean)
        .join(", ");

    const toggleDuplicateDetails = () => {
        setShowDuplicateDetails(true);
    };

    const getDuplicateCustomers = () => {
        const allDuplicates = [];

        if (crDuplicateCustomer && crDuplicateCustomer.length > 0) {
            crDuplicateCustomer.forEach(cust => {
                allDuplicates.push({
                    ...cust,
                    duplicateType: 'CR Number'
                });
            });
        }

        if (vatDuplicateCustomer && vatDuplicateCustomer.length > 0) {
            vatDuplicateCustomer.forEach(cust => {
                allDuplicates.push({
                    ...cust,
                    duplicateType: 'VAT Number'
                });
            });
        }

        return allDuplicates;
    };

    // Table data configuration - All fields as list items
    const tableData = [
        { label: 'Company Name (EN)', value: data.companyNameEn, icon: '🏢' },
        { label: 'Company Name (AR)', value: data.companyNameAr, icon: '🌐' },
        { label: 'ERP Company ID', value: data.erpCustId, icon: '📋' },
        { label: 'Brand Name', value: data.brandNameEn, icon: '🏷️' },
        { label: 'VAT Number', value: data.vatNumber, icon: '💰' },
        { label: 'CR Number', value: data.crNumber, icon: '📄' },
        { label: 'Business Type', value: data.typeOfBusiness, icon: '💼' },
        { label: 'City', value: data.city, icon: '🏙️' },
        { label: 'District', value: data.district, icon: '🗺️' },
        { label: 'Region', value: data.region, icon: '🌍' },
        { label: 'Status', value: data.customerStatus, icon: '📊', isStatus: true },
        { label: 'Address', value: address, icon: '📍', isTextarea: true },
    ];

    const renderDuplicateWarning = () => {
        if (!hasDuplicates) return null;

        const duplicateCustomers = getDuplicateCustomers();

        return (
            <div className="company-duplicate-warning">
                <div className="duplicate-warning-header">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                    <span className="warning-title">{t('Duplicate Records Found')}</span>
                </div>

                <div className="duplicate-counts">
                    {crDuplicateCount > 0 && (
                        <div className="duplicate-count-item">
                            <span className="count-label">{t('CR Number')}:</span>
                            <span className="count-badge">{crDuplicateCount}</span>
                            <span className="count-text">{t('duplicate(s) found')}</span>
                        </div>
                    )}
                    {vatDuplicateCount > 0 && (
                        <div className="duplicate-count-item">
                            <span className="count-label">{t('VAT Number')}:</span>
                            <span className="count-badge">{vatDuplicateCount}</span>
                            <span className="count-text">{t('duplicate(s) found')}</span>
                        </div>
                    )}
                </div>

                {/* <button
                    className="view-duplicates-btn"
                    onClick={toggleDuplicateDetails}
                >
                    <FontAwesomeIcon icon={ faEye} />
                    {t('View Duplicates')}
                </button> */}

                {showDuplicateDetails && (
                    <div className="duplicate-details">
                        {duplicateCustomers.map((dupCustomer, index) => (
                            <div key={index} className="duplicate-customer-item">
                                <div className="duplicate-customer-info">
                                    <span className="duplicate-type-badge">
                                        {dupCustomer.duplicateType}
                                    </span>
                                    <span className="duplicate-customer-name">
                                        {dupCustomer.company_name_en || dupCustomer.companyNameEn || 'N/A'}
                                    </span>
                                </div>
                                <div className="duplicate-customer-details">
                                    <span className="customer-id">ID: {dupCustomer.id}</span>
                                    <span className="customer-erp-id">
                                        ERP: {dupCustomer.erp_cust_id || dupCustomer.erpCustId || 'N/A'}
                                    </span>
                                    {dupCustomer.cr_number && (
                                        <span className="customer-cr">CR: {dupCustomer.cr_number}</span>
                                    )}
                                    {dupCustomer.vat_number && (
                                        <span className="customer-vat">VAT: {dupCustomer.vat_number}</span>
                                    )}
                                    {dupCustomer.assigned_to_name && (
                                        <span className="customer-vat">Sales Excutive: {dupCustomer.assigned_to_name}</span>
                                    )}
                                    <span className={`customer-status-badge ${dupCustomer.customer_status}`}>
                                        {dupCustomer.customer_status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="company-modal-overlay" onClick={onClose}>
            <div
                className="company-modal-content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="customer-modal-title"
            >
                {/* Header */}
                <div className="company-modal-header">
                    <div className="company-modal-header-left">
                        <h3 id="customer-modal-title" className="company-modal-title">
                            {t("Customer Details")}
                        </h3>
                        {user?.userType?.toLowerCase() === "employee" && (
                            <div className="company-erp-badge">
                                {t("ERP ID")}: {customerData.erpCustId ?? "-"}
                            </div>
                        )}
                         {user?.userType?.toLowerCase() === "employee" && (
                            <div className="company-erp-badge">
                                {t("CR Number")}: {customerData.crNumber ?? "-"}
                            </div>
                        )}
                         {user?.userType?.toLowerCase() === "employee" && (
                            <div className="company-erp-badge">
                                {t("Vat Number")}: {customerData.vatNumber ?? "-"}
                            </div>
                        )}
                    </div>
                    <button
                        className="company-modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="company-modal-body">
                 

                  
                        <>
                            {/* Duplicate Warning Section */}
                            {renderDuplicateWarning()}

                        </>
                
                </div>

                {/* Footer */}
                <div className="company-modal-footer">
                    <button
                        type="button"
                        onClick={onClose}
                        className="company-btn company-btn-secondary"
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={() => onContinue(customer)}
                        className="company-btn company-btn-primary"
                        // disabled={!customer}
                    >
                        {t("Continue")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CompanyModal;